import React from 'react'
import { Zap, Wrench, Bug, Gauge, Archive } from 'lucide-react'

/**
 * The five change types are the changelog's only vocabulary.
 *
 * Every change line opens with its type, so the type *is* the label — there are
 * no tinted category panels and no group headings. Colour and icon only
 * reinforce the word, which means the list still reads in greyscale, in a
 * pasted email, or to a colour-blind reader.
 *
 * Colours come straight from the build spec (light / dark) and every one of
 * them already exists in the palette — no new tokens.
 */
export const CHANGE_TYPES = {
  feature:     { label: 'New Feature', icon: Zap,     color: 'text-green-700 dark:text-green-400' },
  improvement: { label: 'Improvement', icon: Wrench,  color: 'text-primary-600 dark:text-primary-300' },
  fix:         { label: 'Bug Fix',     icon: Bug,     color: 'text-blue-600 dark:text-blue-400' },
  performance: { label: 'Performance', icon: Gauge,   color: 'text-violet-600 dark:text-violet-300' },
  deprecated:  { label: 'Deprecated',  icon: Archive, color: 'text-gray-500 dark:text-gray-400' },
}

/**
 * Grouping survives as ordering plus a wider gap between types, so this array
 * is the only thing keeping the list in a predictable order.
 */
export const TYPE_ORDER = ['feature', 'improvement', 'fix', 'performance', 'deprecated']

/** Bucket a release's changes by type, in TYPE_ORDER, dropping empty types. */
export function groupChanges(changes = []) {
  return TYPE_ORDER
    .map((type) => ({ type, items: changes.filter((c) => c.type === type) }))
    .filter((group) => group.items.length > 0)
}

/** "9 September 2026", or "9 Sep 2026" when short — matches the phone layout. */
export function formatReleaseDate(value, { short = false } = {}) {
  if (!value) return ''
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: short ? 'short' : 'long',
    year: 'numeric',
  })
}

/**
 * One type-led change line: icon, the type as a coloured label, then the text.
 */
export function ChangeLine({ change, style }) {
  const type = CHANGE_TYPES[change.type] || CHANGE_TYPES.improvement
  const Icon = type.icon

  return (
    <div className="flex items-start gap-2.5 animate-line-in" style={style}>
      <Icon
        className={`h-[15px] w-[15px] flex-shrink-0 mt-[3px] ${type.color}`}
        strokeWidth={2}
        aria-hidden="true"
      />
      <p
        className="m-0 text-sm leading-[21px] text-gray-700 dark:text-gray-300"
        style={{ textWrap: 'pretty' }}
      >
        <span className={`font-semibold ${type.color}`}>{type.label}:</span>{' '}
        {change.text}
      </p>
    </div>
  )
}

/**
 * A release's whole change body: 10px between lines of one type, 16px between
 * types. `staggerFrom` lets the caller keep the entrance animation running in
 * document order across a card.
 */
export function ChangeGroups({ changes = [], stagger = 0 }) {
  const groups = groupChanges(changes)
  let line = 0

  return (
    <div className="flex flex-col gap-4">
      {groups.map((group) => (
        <div key={group.type} className="flex flex-col gap-2.5">
          {group.items.map((change, i) => (
            <ChangeLine
              key={`${group.type}-${i}`}
              change={change}
              style={stagger ? { animationDelay: `${line++ * stagger}ms` } : undefined}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

/**
 * The monospace version tag. Digits line up down the list; the newest release
 * tints navy and older ones stay neutral, because position in a
 * reverse-chronological list is what actually marks it newest.
 */
export function VersionTag({ version, highlight = false }) {
  return (
    <span
      className={`px-2 py-0.5 rounded-md border font-mono text-xs font-semibold ${
        highlight
          ? 'bg-primary-50 border-primary-100 text-primary-700 dark:bg-primary-700/40 dark:border-primary-700 dark:text-primary-200'
          : 'bg-gray-50 border-gray-200 text-gray-600 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400'
      }`}
    >
      {version}
    </span>
  )
}
